'use client'

import { BrandLogo } from '@/components/brand/BrandLogo'
import { AuthAlternateLink } from '@/components/pages/auth/ui/AuthAlternateLink'
import { AuthBrandingPanel } from '@/components/pages/auth/ui/AuthBrandingPanel'
import { LanguageSwitcher } from '@/components/shared/i18n/LanguageSwitcher'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

const authPanePadX = 'px-6 sm:px-12 lg:px-14 xl:px-20'

export type AuthShellAlternateAccount = {
  prompt: string
  href: string
  linkLabel: string
}

type AuthShellProps = {
  children: ReactNode
  alternateAccount?: AuthShellAlternateAccount
}

export function AuthShell({ children, alternateAccount }: AuthShellProps) {
  const t = useTranslations('auth.shell')
  const year = new Date().getFullYear()

  return (
    <div className="grid min-h-svh w-full bg-zinc-950 lg:grid-cols-2">
      <div className="relative flex min-h-svh flex-col text-zinc-50">
        <header className={cn('flex items-center justify-between gap-4 pt-8', authPanePadX)}>
          <div className="lg:hidden">
            <BrandLogo
              size="sm"
              href="/"
              showWordmark
              wordmarkClassName="text-white"
            />
          </div>

          {alternateAccount ? (
            <div className="ml-auto min-w-0">
              <AuthAlternateLink {...alternateAccount} />
            </div>
          ) : null}
        </header>

        <div className={cn('flex flex-1 flex-col justify-center py-10', authPanePadX)}>
          <div className="mx-auto w-full max-w-md">{children}</div>
        </div>

        <footer className={cn('flex items-center justify-between gap-4 pb-8 pt-4 text-sm text-zinc-500', authPanePadX)}>
          <span>{t('copyright', { year })}</span>
          <LanguageSwitcher />
        </footer>
      </div>

      <div className="relative hidden min-h-svh lg:block">
        <div className="absolute inset-4 lg:inset-6">
          <AuthBrandingPanel />
        </div>
      </div>
    </div>
  )
}
